/// <amd-module name="Wiki/ProjectOverview/WikiAboutRegion" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { Icon } from "VSSUI/Icon";
import { Spinner } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

import { ICancelReason } from "VSS/Core/Util/Promise";
import { FPSLinkComponent } from "VSS/Platform/Components/FPSLink";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { ITfsPageService } from "Tfs/Platform/Page";
import { GitRepository, VersionControlRecursionType } from "Wiki/Clients/VC/Contracts/Git";
import { getGitClient } from "Wiki/Clients/VC/RestClient/Git";
import { WikiPage, WikiPageResponse, WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IWikiRestClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import * as Resources from "Wiki/ProjectOverview/Resources";
import { getWikiReadError, isWikiHomePage, RepoConstants } from "Wiki/ProjectOverview/WikiLegacyHelper";
import { WikiMarkdownRenderer } from "Wiki/Renderer/WikiMarkdownRenderer";

interface IRendererOptions {
    repository?: GitRepository;
    pagePath: string;
    content: string;
}

const enum FetchingState {
    Fetching,
    NotFetching,
    FetchingFailed
}

interface WikiHomePageData {
    repository: GitRepository;
    content: string;
    pagePath: string;
    pageUrl: string;
}

interface IWikiAboutRegionState {
    fetchingState: FetchingState;
    rendererOptions?: IRendererOptions;
    error?: Error;
    homePagePath?: string;
    homePageUrl?: string;
    anchor?: string;
}

export interface IWikiAboutRegionProps extends IVssComponentProperties {
    readMode: boolean;
    repoId: string | undefined;
    onRepositoryChanged: (repoId: string) => void;
    markTimeToInteractive: (source: string) => void;
    showError: boolean;
}

export class WikiAboutRegion extends VssComponent<IWikiAboutRegionProps, IWikiAboutRegionState> {
    private _wikiClient: IWikiRestClient;
    private _ttiMarked = false;
    private readonly _wikiSource = "Wiki";
    private _logSplitTimeInRender = true;
    private readonly _abortSplitTimingName = "Wiki.FetchWikiContent.Abort";

    constructor(props: IWikiAboutRegionProps, context: IVssComponentContext) {
        super(props, context);

        this._wikiClient = this.context.pageContext.getRestClient<IWikiRestClient>("IWikiRestClient");

        if (this.props.readMode) {
            const data = this.context.pageContext
                .getService<IVssContributionService>("IVssContributionService")
                .getData<WikiHomePageData>("ms.vss-wiki-web.wiki-homepage-data-provider-verticals");

            if (data && data.pagePath) {
                this.state = {
                    fetchingState: FetchingState.NotFetching,
                    homePagePath: data.pagePath,
                    homePageUrl: data.pageUrl,
                    rendererOptions: { content: data.content, pagePath: data.pagePath, repository: data.repository }
                };
            }
        }

        if (!this.state) {
            this.state = { fetchingState: FetchingState.Fetching };
        }
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this.props.readMode && this.state.fetchingState === FetchingState.Fetching) {
            this._addSplitTiming("Wiki.GetHomePageData.Start");
            this.trackPromise(
                this.context.pageContext
                    .getService<IVssContributionService>("IVssContributionService")
                    .getDataAsync<WikiHomePageData>(
                        "ms.vss-wiki-web.wiki-homepage-data-provider-verticals",
                        undefined,
                        true /* ignoreCache = true as the component is also used in add-description panel.
                                 We have to fetch latest value from the data provider for the read only mode */
                    )
            ).promise.then(
                data => {
                    this._addSplitTiming("Wiki.GetHomePageData.End");
                    if (data && data.pagePath) {
                        this.setState({
                            fetchingState: FetchingState.NotFetching,
                            homePagePath: data.pagePath,
                            homePageUrl: data.pageUrl,
                            rendererOptions: { content: data.content, pagePath: data.pagePath, repository: data.repository }
                        });
                    } else {
                        this.setState({ fetchingState: FetchingState.FetchingFailed, error: new Error(Resources.WikiErrorTitle) });
                    }
                },
                (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        this._addSplitTiming("Wiki.GetHomePageData.Failed");
                        this._fetchWikiData(this.props, this.context);
                    }
                }
            );
        } else if (!this.props.readMode) {
            this._fetchWikiData(this.props, this.context);
        }
    }

    public componentDidUpdate(prevProps: IWikiAboutRegionProps, prevState: IWikiAboutRegionState): void {
        super.componentDidUpdate(prevProps, prevState);

        // incase of error, content will not get rendered. So we will mark tti here.
        if (!this._ttiMarked && this.props.readMode && (!this.state.rendererOptions || this.state.error)) {
            this.props.markTimeToInteractive(this._wikiSource);
            this._ttiMarked = true;
        }
    }

    public render(): JSX.Element | null {
        if (this.state.fetchingState === FetchingState.Fetching) {
            if (this._logSplitTimeInRender) {
                this._addSplitTiming("Wiki.FetchLegacyComponent.Start");
                this._logSplitTimeInRender = false;
            }
            return this._loadingComponent();
        } else if (this.state.fetchingState === FetchingState.FetchingFailed && this.props.showError && this.state.error) {
            return (
                <div className="wiki-about-error" role="alert">
                    <div className="error-title fontWeightSemiBold">{Resources.WikiErrorTitle}</div>
                    <div className="error-message">{Resources.WikiErrorMessage}</div>
                </div>
            );
        } else if (this.state.fetchingState === FetchingState.NotFetching) {
            return (
                <div className={css("wiki-about-readme-section", this.props.readMode ? "readmode" : "editmode")}>
                    {!this.props.readMode && <div className="about-preview-title">{Resources.PreviewTitle}</div>}
                    {this.props.readMode && this.state.homePagePath && (
                        <>
                            <Icon className="wiki-icon" iconName="Dictionary" />
                            <FPSLinkComponent
                                className="wiki-title link-text fontWeightSemiBold"
                                href={this.state.homePageUrl}
                                telemetrySource="project_overview_about_region_wiki"
                            >
                                {format("{0} / {1}", Resources.WikiLabel, this.state.homePagePath.slice(1))}
                            </FPSLinkComponent>
                        </>
                    )}
                    {this.state.rendererOptions && !!this.state.rendererOptions.repository ? (
                        <WikiMarkdownRenderer
                            content={this.state.rendererOptions.content}
                            projectId={this.state.rendererOptions.repository.project.id}
                            repositoryId={this.state.rendererOptions.repository.id}
                            pagePath={this.state.rendererOptions.pagePath}
                            anchor={this.state.anchor}
                            onAnchorClick={this._updateAnchor}
                            wikiIdentifier={this.state.rendererOptions.repository.name}
                            onInitialRender={this._onContentRendered}
                            skipInternalLinkTransformation={true}
                            isHostedOutsideWikiHub={true}
                        />
                    ) : null}
                </div>
            );
        }

        return null;
    }

    private _onContentRendered = (): void => {
        if (!this._ttiMarked && this.props.readMode) {
            this.props.markTimeToInteractive(this._wikiSource);
            this._ttiMarked = true;
        }
    };

    private _fetchWikiData(props: IWikiAboutRegionProps, context: IVssComponentContext): void {
        this._addSplitTiming("Wiki.FetchWikiContent.Start");

        const tfsPageData = context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        const projectName = tfsPageData && tfsPageData.project && tfsPageData.project.name;

        if (!props.repoId || !projectName) {
            this._addSplitTiming("Wiki.GetAllWikis.Start");

            this.trackPromise(this._wikiClient.getAllWikis(projectName)).promise.then(
                (wikis: WikiV2[]) => {
                    this._addSplitTiming("Wiki.GetAllWikis.End");

                    if (wikis.length === 0) {
                        this._addSplitTiming(this._abortSplitTimingName);

                        this.setState({ fetchingState: FetchingState.FetchingFailed, error: new Error(Resources.WikiErrorTitle) });
                    } else {
                        const projectWiki = wikis.find(wiki => wiki.type === WikiType.ProjectWiki);

                        if (projectWiki) {
                            this.props.onRepositoryChanged(projectWiki.repositoryId);
                            this._fetchWikiRepositoryData(projectWiki.repositoryId, projectWiki.projectId);
                        } else {
                            this._addSplitTiming(this._abortSplitTimingName);

                            this.setState({
                                fetchingState: FetchingState.FetchingFailed,
                                error: new Error(Resources.WikiErrorTitle)
                            });
                        }
                    }
                },
                (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        this._addSplitTiming("Wiki.GetAllWikis.Failed");
                        this.setState({ fetchingState: FetchingState.FetchingFailed, error: undefined });
                    }
                    this._addSplitTiming(this._abortSplitTimingName);
                }
            );
        } else {
            this._fetchWikiRepositoryData(props.repoId, projectName);
        }
    }

    private _fetchWikiRepositoryData(repoId: string, project: string): void {
        this._addSplitTiming("Wiki.GetRepositoryData.Start");

        this.trackPromise(getGitClient(this.context.pageContext).getRepository(repoId, project)).promise.then(
            (repository: GitRepository) => {
                this._addSplitTiming("Wiki.GetRepositoryData.End");
                this._fetchHomePage(repository, project);
            },
            (error: ICancelReason) => {
                if (!error.isCanceled) {
                    this._addSplitTiming("Wiki.GetRepositoryData.Failed");

                    this.setState({ fetchingState: FetchingState.FetchingFailed, error: undefined });
                }
                this._addSplitTiming(this._abortSplitTimingName);
            }
        );
    }

    private _fetchHomePage(repository: GitRepository, project: string): void {
        this._addSplitTiming("Wiki.FetchHomePage.Start");

        this.trackPromise(
            this._wikiClient.getPage(project, repository.id, RepoConstants.RootPath, VersionControlRecursionType.OneLevel)
        ).promise.then(
            (response: WikiPageResponse) => {
                this._addSplitTiming("Wiki.FetchHomePage.End");
                const homePage = response.page.subPages.find(page => isWikiHomePage(page));
                if (homePage) {
                    this._fetchHomePageContent(homePage, project, repository);
                } else {
                    this._addSplitTiming(this._abortSplitTimingName);

                    this.setState({ fetchingState: FetchingState.FetchingFailed, error: new Error(Resources.WikiErrorTitle) });
                }
            },
            (error: ICancelReason) => {
                if (!error.isCanceled) {
                    this._addSplitTiming("Wiki.FetchHomePage.Failed");
                    this.setState({ fetchingState: FetchingState.FetchingFailed, error: undefined });
                }
                this._addSplitTiming(this._abortSplitTimingName);
            }
        );
    }

    private _fetchHomePageContent(homePage: WikiPage, project: string, repository: GitRepository): void {
        this._addSplitTiming("Wiki.FetchHomePageContent.Start");

        this.trackPromise(this._wikiClient.getPageText(project, repository.id, homePage.path)).promise.then(
            (content: string) => {
                this._addSplitTiming("Wiki.FetchWikiContent.End");

                const rendererOptions: IRendererOptions = {
                    content: content,
                    repository: repository,
                    pagePath: homePage.path
                };
                this.setState({
                    error: undefined,
                    fetchingState: FetchingState.NotFetching,
                    rendererOptions: rendererOptions,
                    homePagePath: homePage.path,
                    homePageUrl: homePage.remoteUrl
                });
            },
            (error: Error | ICancelReason) => {
                if (!(error as ICancelReason).isCanceled) {
                    const newError = getWikiReadError(error, homePage.path);
                    this.setState({ fetchingState: FetchingState.FetchingFailed, error: newError });
                }
                this._addSplitTiming(this._abortSplitTimingName);
            }
        );
    }

    private _addSplitTiming(name: string): void {
        if (this._ttiMarked) {
            return;
        }
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.addSplitTiming(name);
    }

    private _loadingComponent = (): JSX.Element => {
        return <Spinner className="about-loading-spinner" />;
    };

    private _updateAnchor = (anchor?: string): void => {
        this.setState({ anchor });
    };
}
